import { create } from "zustand";

import { clearReviewRouteParams } from "./review-navigation";
import type { AppView, Filters, User } from "./types";

type Theme = "cyber" | "base";
const INCLUDE_AUTOMATED_STORAGE_KEY = "sherlog-include-automated-runs";

interface Toast {
  message: string;
  type: "success" | "error";
}

function isTheme(value: unknown): value is Theme {
  return value === "cyber" || value === "base";
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "cyber";
  const stored = window.localStorage.getItem("sherlog-theme");
  return isTheme(stored) ? stored : "cyber";
}

function getInitialIncludeAutomated(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(INCLUDE_AUTOMATED_STORAGE_KEY) === "true";
}

function getInitialActiveView(): AppView {
  if (typeof window === "undefined") return "sessions";

  const url = new URL(window.location.href);
  if (url.searchParams.get("view") !== "github-reviews") return "sessions";
  if (url.searchParams.get("section") !== "codex") return "github-reviews";
  return url.searchParams.get("rule") ? "github-reviews-codex-rule" : "github-reviews-codex";
}

function persistIncludeAutomated(includeAutomated: boolean): void {
  if (typeof window === "undefined") return;
  if (includeAutomated) {
    window.localStorage.setItem(INCLUDE_AUTOMATED_STORAGE_KEY, "true");
  } else {
    window.localStorage.removeItem(INCLUDE_AUTOMATED_STORAGE_KEY);
  }
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.add("theme-transition");
  root.dataset.theme = theme;
  window.setTimeout(() => {
    root.classList.remove("theme-transition");
  }, 220);
}

interface AppState {
  // User state
  user: User | null;
  setUser: (user: User | null) => void;

  // View state
  activeView: AppView;
  activeMoriartyEnvId: string | null;
  activeDocSlug: string | null;
  setView: (view: AppView) => void;
  selectMoriartyEnv: (id: string) => void;
  setDocSlug: (slug: string) => void;
  navigationBlockUrl: string | null;
  setNavigationBlockUrl: (url: string | null) => void;

  // Session state
  activeSessionKey: string | null;
  selectSession: (key: string) => void;
  clearSession: () => void;

  // Sidebar state (for mobile)
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;

  // Docs index state (for mobile)
  docsIndexOpen: boolean;
  openDocsIndex: () => void;
  closeDocsIndex: () => void;

  // Filter state
  filters: Filters;
  setFilter: (key: keyof Filters, value: string | boolean | undefined) => void;
  toggleMine: () => void;
  clearFilters: () => void;

  // Toast state
  toast: Toast | null;
  showToast: (message: string, type: "success" | "error") => void;
  hideToast: () => void;

  // Theme state
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // Composer state
  composerModelId: string | null;
  setComposerModelId: (modelId: string | null) => void;
}

const initialTheme = getInitialTheme();
applyTheme(initialTheme);

const defaultFilters: Filters = { mine: true, includeAutomated: false };
const initialFilters: Filters = {
  ...defaultFilters,
  includeAutomated: getInitialIncludeAutomated(),
};

export const useStore = create<AppState>((set, get) => ({
  // User state
  user: null,
  setUser: (user) =>
    set((state) => ({
      user,
      filters: user?.role === "admin" ? state.filters : { ...state.filters, person: undefined },
    })),

  // View state
  activeView: getInitialActiveView(),
  activeMoriartyEnvId: null,
  activeDocSlug: null,
  navigationBlockUrl: null,
  setNavigationBlockUrl: (navigationBlockUrl) => set({ navigationBlockUrl }),
  setView: (view) => {
    if (get().navigationBlockUrl) return;
    set({
      activeView: view,
      activeMoriartyEnvId: null,
      activeDocSlug: null,
      activeSessionKey: null,
    });
  },
  selectMoriartyEnv: (id) => {
    if (get().navigationBlockUrl) return;
    set({
      activeView: "moriarty-detail",
      activeMoriartyEnvId: id,
      activeDocSlug: null,
      activeSessionKey: null,
    });
    const url = new URL(window.location.href);
    clearReviewRouteParams(url);
    url.searchParams.delete("thread");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    url.searchParams.set("moriarty", id);
    window.history.pushState({}, "", url);
  },
  setDocSlug: (slug) => {
    if (get().navigationBlockUrl) return;
    set({
      activeView: "docs",
      activeDocSlug: slug,
      activeMoriartyEnvId: null,
      activeSessionKey: null,
    });
    const url = new URL(window.location.href);
    clearReviewRouteParams(url);
    url.searchParams.delete("thread");
    url.searchParams.delete("moriarty");
    url.searchParams.set("view", "docs");
    url.searchParams.set("doc", slug);
    window.history.pushState({}, "", url);
  },

  // Session state
  activeSessionKey: null,
  selectSession: (key) => {
    if (get().navigationBlockUrl) return;
    set({
      activeSessionKey: key,
      activeView: "sessions",
      activeMoriartyEnvId: null,
      activeDocSlug: null,
    });
    // Update URL with thread param
    const url = new URL(window.location.href);
    clearReviewRouteParams(url);
    url.searchParams.set("thread", key);
    url.searchParams.delete("moriarty");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    window.history.pushState({}, "", url);
  },
  clearSession: () => {
    if (get().navigationBlockUrl) return;
    set({ activeSessionKey: null });
    // Clear thread param from URL
    const url = new URL(window.location.href);
    clearReviewRouteParams(url);
    url.searchParams.delete("thread");
    url.searchParams.delete("moriarty");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    window.history.pushState({}, "", url);
  },

  // Sidebar state (for mobile)
  sidebarOpen: false,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  closeSidebar: () => set({ sidebarOpen: false }),

  // Docs index state (for mobile)
  docsIndexOpen: false,
  openDocsIndex: () => set({ docsIndexOpen: true }),
  closeDocsIndex: () => set({ docsIndexOpen: false }),

  // Filter state
  filters: initialFilters,
  setFilter: (key, value) => {
    if (key === "includeAutomated") {
      persistIncludeAutomated(value === true);
    }
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value || undefined,
        ...(key === "person" && value ? { mine: undefined } : {}),
        ...(key === "mine" && value ? { person: undefined } : {}),
      },
    }));
  },
  toggleMine: () =>
    set((state) => ({
      filters: {
        ...state.filters,
        mine: !state.filters.mine,
        person: undefined,
      },
    })),
  clearFilters: () => {
    persistIncludeAutomated(false);
    set({ filters: defaultFilters });
  },

  // Toast state
  toast: null,
  showToast: (message, type) => {
    set({ toast: { message, type } });
    setTimeout(() => set({ toast: null }), 3000);
  },
  hideToast: () => set({ toast: null }),

  // Theme state
  theme: initialTheme,
  setTheme: (theme) => {
    set({ theme });
    applyTheme(theme);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("sherlog-theme", theme);
    }
  },
  toggleTheme: () =>
    set((state) => {
      const next = state.theme === "cyber" ? "base" : "cyber";
      applyTheme(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("sherlog-theme", next);
      }
      return { theme: next };
    }),

  // Composer state
  composerModelId: null,
  setComposerModelId: (modelId) => set({ composerModelId: modelId }),
}));
